import React, { useEffect, useRef, useState } from 'react';
import { useAppDispatch } from 'hooks';
import { Label } from 'reactstrap';
import { skipToken } from '@reduxjs/toolkit/query/react';
import { useFormikContext } from 'formik';

import { deriveBaseAvailableAmount, showCaseManagement } from '~support-at-home/lib/derive';

import { Button, ClickableIcon, ConfirmDialog, ErrorResponse, Toolbar } from '~components';
import { Switch } from '~components/form-fields/inputs';
import { InputProps } from '~components/form-fields/props';
import { faRefresh } from '~components/icon';

import type { Supplement } from '~lookups/models';
import type { Budget, Section, SectionItem } from '~support-at-home/models';

import { getAll as getServices } from '~appointments/actions/serviceTypes';
import { getAll as getCalendarFrequencies } from '~lookups/actions/calendarFrequencies';
import { getAll as getSahCategories } from '~lookups/actions/sahCategories';
import { getAll as getShifts } from '~lookups/actions/shifts';
import {
  buildCopySections,
  buildSections,
  useGetContributionsQuery,
  useGetFilteredServicesQuery,
  useRecalculateMutation,
} from '~support-at-home/actions/budgets';

import BudgetDetailsModal from './BudgetDetailsModal';
import BudgetGrandTotal from './BudgetGrandTotal';
import BudgetSection from './BudgetSection';
import BudgetSupplementsModal from './BudgetSupplementsModal';
import BudgetSupplementTotal from './BudgetSupplementTotal';
import BudgetTotalIndicator from './BudgetTotalIndicator';

interface ValueProps extends Partial<Budget> {
  price_plan_deleted: boolean;
  source_budget_id?: string;
}

interface ComponentProps extends InputProps {
  value: ValueProps;
  onChange?: (name: string, value: any) => void;
}

const BudgetSections: React.FC<ComponentProps> = ({ name, value, onChange }) => {
  const dispatch = useAppDispatch();

  // retrieve initial budget from formik context - only needed for edit flows
  const { values } = useFormikContext<{ initial_budget?: Budget }>();
  const initialBudget = values?.initial_budget ?? null;

  const recalcKey = useRef<string | null>(null);
  const isRecalcRunning = useRef(false);
  const pendingSectionsRef = useRef<Section[] | null>(null);
  const debounceTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const [isSupplementsOpen, setIsSupplementsOpen] = useState<boolean>(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState<boolean>(false);
  const [pendingDelete, setPendingDelete] = useState<{ id: string; category_id: string } | null>(null);
  const [copyError, setCopyError] = useState<string | null>(null);

  const {
    id,
    price_plan_revision_id,
    client_id,
    client_sah_budget_id,
    package_fixed_id,
    start_date,
    end_date,
    sections = [],
    total_amount = 0,
    total_allocated = 0,
    total_includes_case_management,
    ignore_sah_filters = false,
    grand_total = { cost: 0, contribution: 0, govSubsidy: 0, case_management: 0 },
    price_plan_deleted = false,
    supplements = [],
    available_amount = 0,
    source_budget_id,
  } = value || {};

  const isNew = id === undefined;

  // INITIALIZE available_amount from derived base amount on mount (before recalc runs)
  useEffect(() => {
    if (available_amount === 0 && total_amount > 0) {
      const derivedAmount = deriveBaseAvailableAmount({
        package_fixed_id,
        total_amount,
        total_includes_case_management,
      });
      if (derivedAmount > 0) {
        onChange?.('available_amount', derivedAmount);
      }
    }
  }, []);

  // PREFILL SECTIONS (on initial render if no sections exist yet)
  useEffect(() => {
    if (!price_plan_revision_id || !package_fixed_id) return;

    if ((value.sections?.length ?? 0) > 0) return;

    // Copy flow: fetch reconciled services from source budget
    if (source_budget_id && client_id) {
      dispatch(
        buildCopySections({
          id: source_budget_id,
          revision_id: price_plan_revision_id,
          client_id: typeof client_id === 'string' ? parseInt(client_id, 10) : client_id,
          package_fixed_id: typeof package_fixed_id === 'string' ? parseInt(package_fixed_id, 10) : package_fixed_id,
          start_date: start_date ?? '',
          end_date: end_date ?? '',
        }),
      )
        .then((res: { sections: Section[]; supplements: Supplement[] }) => {
          if (res?.sections?.length > 0) {
            onChange?.(name, res.sections);
            if (res.supplements?.length > 0) {
              onChange?.('supplements', res.supplements);
            }
          }
        })
        .catch((err) => {
          console.error('buildCopySections failed', err);
          setCopyError('Failed to load services from source budget. Please try again or contact support.');
        });
      return;
    }

    // New budget flow: fetch prefilled services
    dispatch(
      buildSections({
        revision_id: price_plan_revision_id,
        client_id,
        package_fixed_id,
        start_date,
        end_date,
      }),
    )
      .then((res: any) => {
        if (Array.isArray(res) && res.length > 0) {
          onChange?.(name, res);
        }
      })
      .catch((err) => console.error('buildSections failed', err));
  }, [price_plan_revision_id, package_fixed_id, start_date, end_date]);

  // LOOKUP DATA LOADS
  useEffect(() => {
    dispatch(getServices({ deleted: [true, false] }));
    dispatch(getSahCategories());
    dispatch(getCalendarFrequencies());
    dispatch(getShifts());
  }, []);

  // FILTERED SERVICES QUERY
  const clientId = typeof client_id === 'string' ? parseInt(client_id, 10) : client_id;
  const pkgFixedId = typeof package_fixed_id === 'string' ? parseInt(package_fixed_id, 10) : package_fixed_id;

  const { data: filteredServices } = useGetFilteredServicesQuery(
    price_plan_revision_id
      ? { revision_id: price_plan_revision_id, client_id: clientId, package_fixed_id: pkgFixedId, ignore_sah_filters }
      : skipToken,
  );

  // CONTRIBUTIONS
  const {
    data: contributions,
    refetch: refetchContributions,
    isFetching: isRefreshingContributions,
  } = useGetContributionsQuery(
    client_id && package_fixed_id && start_date && end_date
      ? { client_id, package_fixed_id: pkgFixedId, start_date, end_date }
      : skipToken,
  );

  // RECALCULATE LOGIC
  const [recalculate] = useRecalculateMutation();

  const normalizeNumericValues = (item: any) => {
    const normalized = { ...item };
    if (typeof normalized.unit_amount === 'string') normalized.unit_amount = Number(normalized.unit_amount);
    if (typeof normalized.quantity === 'string') normalized.quantity = Number(normalized.quantity);
    return normalized;
  };

  const runRecalc = async (sectionsSnapshot: Section[]) => {
    isRecalcRunning.current = true;
    try {
      const normalizedSections = sectionsSnapshot.map((section) => ({
        ...section,
        items: section.items.map((item) => normalizeNumericValues(item)),
      }));

      const result = await recalculate({
        sections: normalizedSections,
        price_plan_revision_id,
        start_date,
        end_date,
        total_amount,
        total_includes_case_management,
        supplements,
        client_sah_budget_id,
      }).unwrap();

      const serverSections = result.sections ?? result.data?.sections ?? [];
      const serverGrandTotal = result.grand_total ?? result.data?.grand_total ?? {};
      const serverSupplements = result.supplements ?? result.data?.supplements ?? [];
      const serverAvailableAmount = result.available_amount ?? result.data?.available_amount ?? 0;

      const mergedSections = sectionsSnapshot.map((existingSection) => {
        const updatedSection = serverSections.find((sec: Section) => sec.category_id === existingSection.category_id);
        if (!updatedSection) return existingSection;

        const updatedItems = existingSection.items.map((item) => {
          const incoming = updatedSection.items.find((i: SectionItem) => i.id === item.id);
          if (!incoming) return item;
          return {
            ...item,
            billing_type: incoming.billing_type ?? item.billing_type,
            unit_amount: incoming.unit_amount ?? item.unit_amount,
            unit_amount_ph: incoming.unit_amount_ph ?? item.unit_amount_ph,
            total_amount: incoming.total_amount ?? 0,
            copy_status: item.copy_status,
            previous_unit_amount: item.previous_unit_amount,
          };
        });

        return {
          ...existingSection,
          items: updatedItems,
          totals: updatedSection.totals ?? existingSection.totals,
          service_totals: updatedSection.service_totals ?? existingSection.service_totals,
        };
      });

      onChange?.(name, mergedSections);
      onChange?.('grand_total', serverGrandTotal);
      onChange?.('supplements', serverSupplements);
      onChange?.('available_amount', serverAvailableAmount);
    } catch (err) {
      console.error('Recalculate failed:', err);
    } finally {
      isRecalcRunning.current = false;
      if (pendingSectionsRef.current) {
        const next = pendingSectionsRef.current;
        pendingSectionsRef.current = null;
        runRecalc(next);
      }
    }
  };

  const debouncedRecalculate = (sectionsData: Section[]) => {
    if (debounceTimer.current) clearTimeout(debounceTimer.current);

    debounceTimer.current = setTimeout(() => {
      if (isRecalcRunning.current) {
        pendingSectionsRef.current = sectionsData;
        return;
      }
      runRecalc(sectionsData);
    }, 600);
  };

  useEffect(() => {
    if (!price_plan_revision_id) return;

    // Filter out new / incomplete items that shouldn't trigger recalculation
    const costRelevantSections = sections.map((section) => ({
      category_id: section.category_id,
      contrib_proportion: section.contrib_proportion,
      items: section.items
        .filter((item) => !!item.service_type_id)
        .map((item) => ({
          service_type_id: item.service_type_id,
          start_date: item.start_date,
          end_date: item.end_date,
          week_days: item.week_days,
          shift_id: item.shift_id,
          frequency_id: item.frequency_id,
          quantity: item.quantity,
          unit_amount: item.unit_amount,
          include_public_holidays: item.include_public_holidays,
        })),
    }));

    // Only include supplement properties that should TRIGGER recalc (add/remove)
    // Exclude computed outputs like amount, rate, timestamps
    // Sort by supplement_id to ensure consistent ordering
    const costRelevantSupplements = supplements
      .map((sup: any) => ({
        supplement_id: sup.supplement_id,
      }))
      .sort((a, b) => a.supplement_id.localeCompare(b.supplement_id));

    // Skip recalc when total_amount is missing — the API requires it and there's nothing to calculate
    if (!total_amount && total_amount !== 0) return;

    const costRelevantKey = JSON.stringify({
      price_plan_revision_id,
      sections: costRelevantSections,
      supplements: costRelevantSupplements,
      total_amount,
      total_includes_case_management,
      client_sah_budget_id,
    });

    // Skip recalc if nothing cost-relevant changed
    if (recalcKey.current === costRelevantKey) return;

    recalcKey.current = costRelevantKey;
    debouncedRecalculate(sections);
  }, [sections, price_plan_revision_id, supplements, total_amount, total_includes_case_management, client_sah_budget_id]);

  useEffect(() => {
    if (!contributions || !Array.isArray(contributions)) return;

    const updatedSections = sections.map((section) => {
      const contrib = contributions.find((con) => con.category_id === section.category_id);
      if (!contrib) return section;
      return {
        ...section,
        contrib_proportion: contrib.contrib_proportion ?? section.contrib_proportion,
      };
    });

    onChange?.(name, updatedSections);
  }, [contributions]);

  // Cleanup timers on unmount
  useEffect(() => {
    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
        debounceTimer.current = null;
      }
    };
  }, []);

  const handleUpdate = (category_id: string, items: SectionItem[]) => {
    const sectionIndex = sections.findIndex((section) => section.category_id === category_id);
    if (sectionIndex === -1) return;

    const updatedSections = [...sections];
    updatedSections[sectionIndex] = {
      ...updatedSections[sectionIndex],
      items,
    };

    onChange?.(name, updatedSections);
  };

  // Delete handler & confirmation modal callback

  const confirmDelete = (confirmed: boolean) => {
    if (confirmed && pendingDelete) {
      const { id, category_id } = pendingDelete;

      const updatedSections = sections.map((section) =>
        section.category_id === category_id
          ? { ...section, items: section.items.filter((item) => item.id !== id) }
          : section,
      );

      onChange?.(name, updatedSections);
    }
    setPendingDelete(null);
  };

  const handleDelete = (sectionId: string, item: SectionItem) => {
    setPendingDelete({ id: item.id, category_id: sectionId });
  };

  const handleUpdateContribProportion = async () => {
    if (!client_id || !package_fixed_id || !start_date || !end_date) return;

    try {
      const { data: refreshed } = await refetchContributions();
      if (!refreshed || !Array.isArray(refreshed)) return;

      // Merge new contribution proportions into existing sections
      const updatedSections = sections.map((section) => {
        const contrib = refreshed.find((c) => c.category_id === section.category_id);
        if (!contrib) return section;
        return {
          ...section,
          contrib_proportion: contrib.contrib_proportion ?? section.contrib_proportion,
        };
      });

      onChange?.(name, updatedSections);
    } catch (err) {
      console.error('Failed to refresh contributions', err);
    }
  };

  return (
    <div>
      <ConfirmDialog
        isOpen={!!pendingDelete}
        messages={
          <p>
            <strong>Warning:</strong> This record will be deleted.
          </p>
        }
        onClose={confirmDelete}
        useList={false}
      />
      <BudgetSupplementsModal
        isOpen={isSupplementsOpen}
        setIsOpen={setIsSupplementsOpen}
        value={{ supplements, package_fixed_id }}
        onChange={onChange}
      />
      <BudgetDetailsModal isOpen={isDetailsOpen} setIsOpen={setIsDetailsOpen} values={value} onChange={onChange} />
      {/* Outer Container for all sections */}
      <div className="budget-container">
        <div className="budget-sections">
          <BudgetTotalIndicator
            allocated={grand_total?.cost ?? total_allocated ?? 0}
            available={available_amount}
            client_id={client_id}
          />
          <Toolbar
            id="budget-toolbar"
            leftCluster={
              <>
                <div className="d-flex justify-content-end align-items-center ms-4">
                  <Label className="me-3 fw-bold" for="ignore-sah-filters">
                    Ignore SAH Filters:
                  </Label>
                  <Switch
                    type="switch"
                    id="ignore-sah-filters"
                    name="ignore-sah-filters"
                    value={ignore_sah_filters}
                    onChange={() => onChange?.('ignore_sah_filters', !ignore_sah_filters)}
                  />
                </div>
              </>
            }
            rightCluster={
              !isNew ? (
                <>
                  <Button id="supplements-button" onClick={() => setIsSupplementsOpen(true)} size="sm">
                    Edit Supplements
                  </Button>
                  <Button id="edit-button" onClick={() => setIsDetailsOpen(true)} size="sm" className="ms-2">
                    Edit Budget Details
                  </Button>
                  <ClickableIcon
                    id="refresh-values-btn"
                    toolTip="Refresh Co-Contributions"
                    icon={faRefresh}
                    onClick={handleUpdateContribProportion}
                    disabled={isRefreshingContributions}
                  />
                </>
              ) : undefined
            }
          />

          {copyError && <ErrorResponse message={copyError} />}
          {sections.map((section: Section) => {
            const initialValues = initialBudget?.sections?.find(
              ({ category_id }) => category_id === section.category_id,
            );
            return (
              <BudgetSection
                key={section.category_id}
                id={section.category_id}
                section={section}
                initialValues={initialValues}
                start_date={start_date}
                end_date={end_date}
                readonly={price_plan_deleted}
                filteredServices={filteredServices ?? []}
                onUpdate={handleUpdate}
                onDelete={handleDelete}
              />
            );
          })}
        </div>
        <div className="totals-container">
          <BudgetSupplementTotal id="supplements-grand-total" supplements={supplements} />
          <BudgetGrandTotal
            id="sections-grand-total"
            showCaseManagement={showCaseManagement({ package_fixed_id })}
            totals={grand_total}
            amount={total_amount}
            supplements={supplements}
            includesCaseManagement={total_includes_case_management}
          />
        </div>
      </div>
    </div>
  );
};

export default BudgetSections;
